<template>
    <UButton variant="link" class="flex items-center gap-2" icon="i-solar-user-line-duotone" color="black"
        @click="openModal = true">
        <span class="whitespace-nowrap"> ورود/ثبت‌نام </span>
    </UButton>
    <UModal v-model="openModal" prevent-close>
        <div class="absolute left-4 top-4">
            <UButton icon="i-solar-close-circle-line-duotone" color="rose" variant="link" @click="openModal = false" />
        </div>
        <div class="max-w-96 w-full mx-auto relative">
            <div class="flex justify-center my-8">
                <UiLogo />
            </div>
            <Transition mode="out-in">
                <div v-if="type === 'create'">
                    <LazyUtilsCreateAccount :username="username" :is-loading="isLoading" @send-data="handleRegister"
                        @change:type="handleChangeType" />
                </div>
                <div v-else-if="type === 'login'">
                    <UtilsLoginToAccount :is-loading="isLoading" @send-data="handleLogin"
                        @change:type="handleChangeType" />
                </div>
                <div v-else-if="type === 'otp'">
                    <LazyUtilsOtpPassword :username="username" @change:type="handleChangeType" />
                </div>
                <div v-else-if="type === 'forgot'">
                    <LazyUtilsForgotPassword :username="username" @change:type="handleChangeType" />
                </div>
            </Transition>
        </div>
    </UModal>
</template>

<script lang="ts" setup>
import type { IDynamicKey } from "~/libs/types/general";

const { login, isAuthenticated } = useSanctumAuth();

const emits = defineEmits(["update:login"]);

const openModal = ref(false);
const isLoading = ref(false);
const username: Ref<string | undefined> = ref(undefined);

type ModalType = "create" | "login" | "forgot" | "otp";

const type: Ref<ModalType> = ref("login");

const toast = useToast();
const { t } = useI18n();

interface ICredentials {
    username: string;
    password: string;
}

defineShortcuts({
    escape: {
        usingInput: true,
        whenever: [openModal],
        handler: () => {
            openModal.value = false;
        },
    },
});

async function handleLogin(credentials: ICredentials) {
    isLoading.value = true;

    try {
        if (!isAuthenticated.value) {
            await login(credentials);
            openModal.value = false;
            emits("update:login");
        }
    } catch (error) {
        const err = useSanctumError(error);
        if (err.isValidationError) {
            toast.add({
                timeout: 5000,
                color: "rose",
                title: t("error"),
                description: t(err.bag[0].message),
                icon: "i-solar-danger-broken",
            });
        }
    } finally {
        isLoading.value = false;
    }
}

async function handleRegister(e: IDynamicKey<string>) {
    isLoading.value = true;

    try {
        await fetchData("/auth/register", {
            method: "POST",
            useSanctum: true,
            body: e,
        });
        type.value = "otp";
    } catch (error) {
        const err = useSanctumError(error);
        if (err.isValidationError) {
            toast.add({
                timeout: 5000,
                color: "rose",
                title: t("error"),
                description: t(err.bag[0].message),
                icon: "i-solar-danger-broken",
            });
        }
    } finally {
        isLoading.value = false;
    }
}

function handleChangeType(e: ModalType, data?: string) {
    type.value = e;
    if (data) {
        username.value = data;
    }
}
</script>

<style scoped>
.v-enter-active,
.v-leave-active {
    transition: all 0.5s ease;
}

.v-enter-from {
    opacity: 0;
    transform: translateX(50px);
}

.v-leave-to {
    opacity: 0;
    transform: translateX(-50px);
}
</style>
